// Severity scale: critical red, serious orange, moderate yellow, minor neutral.
// Yellow cannot carry white text at AA, so moderate flips to dark ink instead.
const IMPACT_STYLES = {
  critical: "bg-[#C62828] text-white",
  serious: "bg-[#C2410C] text-white",
  moderate: "bg-[#FACC15] text-ink",
  minor: "bg-gray-200 text-ink",
};

export const ImpactBadge = ({ impact }) => (
  <span className={`inline-block px-2 py-0.5 text-xs font-mono uppercase tracking-wide rounded-sm ${IMPACT_STYLES[impact] || IMPACT_STYLES.minor}`}>
    {impact}
  </span>
);

const STATUS_STYLES = {
  open: "border-[#C62828] text-[#C62828]",
  fixed: "border-[#2E7D32] text-[#2E7D32]",
  wont_fix: "border-gray-400 text-ink-muted",
  false_positive: "border-[#F57C00] text-[#B45309]",
};

const STATUS_LABELS = { open: "Open", fixed: "Fixed", wont_fix: "Won't fix", false_positive: "False positive" };

export const StatusBadge = ({ status }) => (
  <span className={`inline-block px-2 py-0.5 text-xs font-mono border rounded-sm bg-white ${STATUS_STYLES[status] || ""}`}>
    {STATUS_LABELS[status] || status}
  </span>
);

/* The manual checklist result, and the same four states section 6 of the
 * conformance report prints. Colour alone used to separate them, which WCAG
 * 1.4.1 rules out: green, red and two greys read as one column to anyone who
 * cannot tell them apart. Each state now carries a shape too, drawn in
 * currentColor so it takes the badge's own tone.
 *
 * The shapes and their labels match backend/report_status.py and the shared
 * web report, so a criterion marked here looks the same in the PDF a client is
 * sent. The mark is labelled rather than hidden, so the state reaches a reader
 * who is given the shape and not the word.
 *
 * Colours are measured on the badge's own white fill: #2E7D32 is 5.13:1 and
 * #C62828 is 5.62:1, both clearing 1.4.3 for the 12px label. */
const CHECK_MARK_LABEL = { pass: "Pass", fail: "Fail", na: "Not applicable", untested: "Not yet evaluated" };

const CheckStatusMark = ({ status }) => {
  const label = CHECK_MARK_LABEL[status];
  if (!label) return null;
  return (
    <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.5" role="img" aria-label={label}>
      {status === "pass" && (
        <path d="M2.6 8.4 6.2 12l7.2-8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      )}
      {status !== "pass" && <circle cx="8" cy="8" r="6.5" />}
      {status === "fail" && <path d="M8 4.6v4.1" strokeLinecap="round" />}
      {status === "na" && <path d="M5.1 8h5.8" strokeLinecap="round" />}
      {status === "untested" && (
        <path d="M6.05 6.25C6.05 5.1 6.92 4.35 8 4.35C9.15 4.35 9.95 5.05 9.95 6.1C9.95 7.05 9.3 7.45 8.6 7.9C8.15 8.2 8 8.55 8 9.05V9.3"
          strokeLinecap="round" strokeLinejoin="round" />
      )}
      {(status === "fail" || status === "untested") && (
        <circle cx="8" cy={status === "fail" ? 11.3 : 11.35} r="0.95" fill="currentColor" stroke="none" />
      )}
    </svg>
  );
};

export const CheckStatusBadge = ({ status }) => {
  const styles = {
    pass: "border-[#2E7D32] text-[#2E7D32]",
    fail: "border-[#C62828] text-[#C62828]",
    na: "border-gray-400 text-ink-muted",
    untested: "border-gray-300 text-ink-subtle",
  };
  const labels = { pass: "Pass", fail: "Fail", na: "N/A", untested: "Untested" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono border rounded-sm bg-white ${styles[status]}`}>
      <CheckStatusMark status={status} />
      {labels[status]}
    </span>
  );
};
